import React, { useEffect, useState } from "react";
import { Avatar } from "@nextui-org/react";
import { apiFetch } from "../../../utils/authFetch";
import { API_URL } from "../../../config/api";
import UserBadges from "../../components/userBadges";
import RankingBadge from "../../components/rankingBadge";
import { SocialLinksDisplay } from "../../components/socialLinks";

function absoluteUpload(url) {
  if (!url) return null;
  return url.startsWith("http") ? url : `${API_URL}${url}`;
}

// Shown while the live lookup is in flight (or if it fails) so the author
// header never flashes empty/blank before the real avatar loads.
const FALLBACK_AVATAR = "https://i.imgur.com/5ITyShg.jpeg";

// The docs content objects (introductionPage, installationPage, ...) are
// plain data, not components, so this is remounted every time the reader
// switches docs page. A tiny in-memory cache keyed by nickname avoids
// re-fetching the same author over and over while browsing the docs.
const authorCache = new Map();

// Live author header for the docs pages: fetches the real account behind
// `nickname` (same public endpoint as the profile page) so display name,
// avatar, badges (role + extra, e.g. Verified/Admin), activity level and
// social links always reflect the live account instead of being
// hardcoded per page. @nickname and the avatar both link to /@nickname.
export default function DocsAuthor({ nickname, publishedAt }) {
  const [data, setData] = useState(() => authorCache.get(nickname) || null);

  useEffect(() => {
    if (authorCache.has(nickname)) return;
    let cancelled = false;
    apiFetch(`/api/users/${encodeURIComponent(nickname)}`, { auth: false })
      .then((d) => {
        if (cancelled) return;
        authorCache.set(nickname, d);
        setData(d);
      })
      .catch(() => {
        // Silently keep showing just the nickname if the lookup fails —
        // a docs page should never break because the profile API is down.
      });
    return () => {
      cancelled = true;
    };
  }, [nickname]);

  const user = data && data.user;
  const profileHref = `/@${nickname}`;

  return (
    <section id="author">
      <div style={{ display: "flex", alignItems: "center", gap: "10px", flexWrap: "wrap" }}>
        <a href={profileHref} aria-label={`View ${nickname}'s profile`}>
          <Avatar
            src={(user && absoluteUpload(user.avatar_url)) || FALLBACK_AVATAR}
            name={(user && user.display_name) || nickname}
            size="md"
          />
        </a>
        <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
          <div style={{ display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap" }}>
            <a href={profileHref} style={{ fontWeight: 600 }}>
              {(user && user.display_name) || nickname}
            </a>
            <a href={profileHref} style={{ color: "#F25081", textDecoration: "underline" }}>
              @{nickname}
            </a>
            {user && <UserBadges role={user.role} badges={user.badges} size="sm" />}
            {data && data.ranking && <RankingBadge ranking={data.ranking} compact />}
          </div>
          {user && user.social_links && user.social_links.length > 0 && (
            <SocialLinksDisplay links={user.social_links} />
          )}
        </div>
      </div>
      <p style={{ fontSize: "0.9em", color: "#666", marginTop: "8px" }}>Published on {publishedAt}</p>
    </section>
  );
}
